import { getControllerApiVersion } from './utils.js';

export const CHARACTERISTIC_INFO_GRAPH_API_MIN = 'BAH';
export const INFO_GRAPH_UNAVAILABLE_VALUE = 0xffff;
export const INFO_GRAPH_SPIKE_FACTOR = 5;

export function supportsCharacteristicInfoGraphs(controller) {
  return getControllerApiVersion(controller).localeCompare(CHARACTERISTIC_INFO_GRAPH_API_MIN) >= 0;
}

export function suppressFactorSpikes(values, factor = INFO_GRAPH_SPIKE_FACTOR) {
  const filtered = [...(values ?? [])];
  if (factor <= 1 || filtered.length < 3) return filtered;
  for (let index = 1; index + 1 < filtered.length; index += 1) {
    const previous = Number(values[index - 1]);
    const current = Number(values[index]);
    const next = Number(values[index + 1]);
    if (
      [previous, current, next].some(
        (value) => !Number.isFinite(value) || value === INFO_GRAPH_UNAVAILABLE_VALUE
      )
    )
      continue;
    const reference = (previous + next) / 2;
    if (reference > 0 && (current > reference * factor || current * factor < reference)) {
      filtered[index] = Math.round(reference);
    }
  }
  return filtered;
}

export function selectCharacteristicIndices(series, maxPoints) {
  const available = (series ?? [])
    .filter((values) => Array.isArray(values) && values.length > 0)
    .map((values) => suppressFactorSpikes(values));
  if (available.length === 0 || maxPoints <= 0) return [];
  const sourceCount = Math.min(...available.map((values) => values.length));
  if (sourceCount <= maxPoints) return Array.from({ length: sourceCount }, (_, index) => index);

  const ranges = available.map((values) => {
    const valid = values.slice(0, sourceCount).filter((value) => value !== INFO_GRAPH_UNAVAILABLE_VALUE);
    if (valid.length === 0) return null;
    const minimum = Math.min(...valid);
    return { minimum, span: Math.max(1, Math.max(...valid) - minimum) };
  });
  const selected = [0, sourceCount - 1];

  while (selected.length < maxPoints) {
    let bestError = 0;
    let bestIndex = -1;
    let bestInsertPosition = 0;
    for (let segment = 0; segment + 1 < selected.length; segment += 1) {
      const start = selected[segment];
      const end = selected[segment + 1];
      for (let index = start + 1; index < end; index += 1) {
        const position = (index - start) / (end - start);
        let pointError = 0;
        available.forEach((values, seriesIndex) => {
          const range = ranges[seriesIndex];
          if (
            !range ||
            [values[start], values[index], values[end]].includes(INFO_GRAPH_UNAVAILABLE_VALUE)
          )
            return;
          const expected =
            (values[start] - range.minimum) / range.span +
            (((values[end] - values[start]) / range.span) * position);
          const actual = (values[index] - range.minimum) / range.span;
          pointError = Math.max(pointError, Math.abs(actual - expected));
        });
        if (pointError > bestError) {
          bestError = pointError;
          bestIndex = index;
          bestInsertPosition = segment + 1;
        }
      }
    }
    if (bestIndex < 0 || bestError <= Number.EPSILON) break;
    selected.splice(bestInsertPosition, 0, bestIndex);
  }
  return selected;
}

export function sampleGraphValues(values, indices) {
  return (indices ?? []).map((index) => values?.[index] ?? INFO_GRAPH_UNAVAILABLE_VALUE);
}

export function graphPoints(indices, values) {
  return (indices ?? []).map((index, position) => ({ x: index, y: values?.[position] ?? null }));
}

export function getRenderableGraphPoints(indices, values, unavailableValue = -1) {
  if (!Array.isArray(indices) || !Array.isArray(values) || values.length < 3) return [];

  return indices
    .map((index, position) => ({ x: Number(index), y: Number(values[position]) }))
    .filter(
      ({ x, y }) => Number.isFinite(x) && Number.isFinite(y) && y !== unavailableValue
    );
}

export function getInfoMiniGraphGeometry(indices, values, unavailableValue = -1) {
  const points = getRenderableGraphPoints(indices, values, unavailableValue);
  if (points.length < 3) return null;

  const timelineIndices = (indices ?? [])
    .slice(0, Array.isArray(values) ? values.length : 0)
    .map(Number)
    .filter(Number.isFinite);
  if (timelineIndices.length === 0) return null;

  const yValues = points.map(({ y }) => y);
  return {
    points,
    minX: Math.min(...timelineIndices),
    maxX: Math.max(...timelineIndices),
    minY: Math.min(...yValues),
    maxY: Math.max(...yValues)
  };
}

export function formatGraphAge(index, sourceSampleCount, sampleIntervalSeconds) {
  const seconds = Math.max(0, (sourceSampleCount - 1 - index) * sampleIntervalSeconds);
  if (seconds < 60) return 'now';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return hours > 0 ? `${hours}h ${minutes}m ago` : `${minutes}m ago`;
}

export function formatGraphSpan(sourceSampleCount, sampleIntervalSeconds, fallback = '24h') {
  if (sourceSampleCount <= 1 || sampleIntervalSeconds <= 0) return fallback;

  const minutes = Math.round(((sourceSampleCount - 1) * sampleIntervalSeconds) / 60);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.round(minutes / 60)}h`;
}

export function formatSelectedGraphSpan(indices, sourceSampleCount, sampleIntervalSeconds) {
  const timeline = (indices ?? []).map(Number).filter((index) => Number.isFinite(index) && index >= 0);
  const span = timeline.length > 1 ? Math.max(...timeline) - Math.min(...timeline) : 0;
  return formatGraphSpan(span > 0 ? span + 1 : sourceSampleCount, sampleIntervalSeconds);
}

function hasValidGraphValues(series, minimumCount) {
  if (!Array.isArray(series)) return false;

  return (
    series.filter((value) => {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) && numericValue !== 0xffff && numericValue !== -1;
    }).length >= minimumCount
  );
}

export function infoGraphHasRenderableSamples(graph) {
  if (graph == null) return false;

  const hasClimateGraph = [graph.humidity_graph, graph.temp_graph].some((series) =>
    hasValidGraphValues(series, 6)
  );
  const hasRootZoneGraph = [
    ...(graph.vol_water_content_graphs ?? []),
    ...(graph.ec_root_zone_graphs ?? [])
  ].some((series) => hasValidGraphValues(series, 3));

  return hasClimateGraph || hasRootZoneGraph;
}
